import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema, subscriptionStatusSchema } from "./_envelope";

/**
 * Daily point-in-time snapshot of how many subscribers are currently in each
 * subscription state, computed from Stripe (the source of truth) by a cron.
 *
 * Unlike `subscription_status_updated` (which fires on every transition and so
 * counts state *changes*), this event is a census: one fire per distinct
 * (status, cancels_at_period_end, has_paid, product_key, billing_period)
 * combination per run, where `value` is the count of distinct people in that
 * combination. Each person is reduced to a single current state (deduped by
 * email) before counting, so the buckets are mutually exclusive — summing
 * `value` grouped by `status` (and optionally the booleans / product / cadence)
 * gives a complete, non-double-counting breakdown, including silently-churned
 * people who never reopen the app.
 *
 * Charted as: sum of `value` grouped by `metadata.status` (and/or
 * `cancels_at_period_end` / `has_paid` / `product_key` / `billing_period`).
 */
export const subscriptionStatusCensus = defineEvent({
  eventName: "subscription_status_census",
  // v3: adds product_key + billing_period to each bucket (v2 added the raw
  // status + booleans). Hard cutover — internal event, no dual-fire.
  eventVersion: 3,
  // Count of distinct people in this bucket combination.
  valueSchema: z.number().int().nonnegative(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      // Raw Stripe status this count is for.
      status: subscriptionStatusSchema.or(z.string()),
      // Whether these subs are scheduled to cancel at period end.
      cancels_at_period_end: z.boolean().optional(),
      // Whether real money has been received (lets `active` split into
      // truly-paying vs comp / unpaid).
      has_paid: z.boolean().optional(),
      // Normalized product key + billing cadence of the bucket's subs.
      product_key: z.string().optional(),
      billing_period: z.string().optional(),
      // ISO date (YYYY-MM-DD) of the snapshot, for grouping/dedup.
      snapshot_date: z.string().optional(),
      // Total people scanned across all buckets this run (sanity check; the
      // per-bucket `value`s should sum to this).
      n_total: z.number().int().nonnegative().optional(),
      // People skipped as internal (staff/test email domains).
      n_excluded_internal: z.number().int().nonnegative().optional(),
    })
    .passthrough(),
});
